export type StoreProvider = 'shopify' | 'woocommerce';
export type ErpProvider = 'rivhit';
export type FlowDirection = 'store_to_erp' | 'erp_to_store' | 'both';

export interface ProviderDef<K extends string> {
  key: K;
  label: string;
  description: string;
  /** company_integrations.provider_name values that satisfy this provider */
  integrationProviders: string[];
  available: boolean;
  settingsPath: string;
}

export const STORE_PROVIDERS: ProviderDef<StoreProvider>[] = [
  {
    key: 'shopify',
    label: 'Shopify',
    description: 'חנות אונליין בשופיפיי',
    integrationProviders: ['shopify', 'shopify_manual'],
    available: true,
    settingsPath: '/app/settings/integrations/shopify',
  },
  {
    key: 'woocommerce',
    label: 'WordPress / WooCommerce',
    description: 'חנות וורדפרס עם ווקומרס',
    integrationProviders: ['woocommerce'],
    available: false,
    settingsPath: '/app/settings/integrations/woocommerce',
  },
];

export const ERP_PROVIDERS: ProviderDef<ErpProvider>[] = [
  {
    key: 'rivhit',
    label: 'רווחית',
    description: 'הנהלת חשבונות והפקת מסמכים',
    integrationProviders: ['rivhit'],
    available: true,
    settingsPath: '/app/settings/integrations/rivhit',
  },
];

export interface OptionChoice {
  value: string;
  label: string;
  description?: string;
}

interface OptionBase {
  key: string;
  label: string;
  help?: string;
  showWhen?: { key: string; equals: string | boolean | string[] };
}

export type FlowOptionDef =
  | (OptionBase & { type: 'choice'; choices: OptionChoice[]; default: string })
  | (OptionBase & { type: 'toggle'; default: boolean })
  | (OptionBase & { type: 'number'; default: number; min?: number; suffix?: string })
  | (OptionBase & { type: 'text'; default: string; placeholder?: string; inputType?: 'text' | 'email' | 'tel' })
  | (OptionBase & { type: 'warehouse_priority'; default: WarehousePref[] })
  | (OptionBase & { type: 'warehouse_multi'; default: number[] })
  | (OptionBase & { type: 'sku_list'; default: string });

export interface WarehousePref { id: number; name: string; enabled: boolean }

export type FlowOptionValue = string | number | boolean | WarehousePref[] | number[];
export type FlowOptionValues = Record<string, FlowOptionValue>;

export interface FlowDef {
  key: string;
  title: string;
  description: string;
  allowedDirections: [FlowDirection, ...FlowDirection[]];
  status: 'available' | 'coming_soon';
  options: FlowOptionDef[];
}

export const DIRECTION_LABELS: Record<FlowDirection, string> = {
  store_to_erp: 'מהחנות למערכת הניהול',
  erp_to_store: 'ממערכת הניהול לחנות',
  both: 'שני הכיוונים',
};

export const FLOWS: FlowDef[] = [
  {
    key: 'order_to_invoice',
    title: 'הפקת חשבונית מהזמנה',
    description: 'כל הזמנה בחנות מפיקה מסמך אוטומטי במערכת הניהול.',
    allowedDirections: ['store_to_erp'],
    status: 'available',
    options: [
      {
        key: 'trigger', type: 'choice', label: 'מתי יוצאת החשבונית', default: 'on_paid',
        choices: [
          { value: 'on_created', label: 'כשההזמנה נוצרת', description: 'מיד עם קבלת ההזמנה בחנות' },
          { value: 'on_paid', label: 'כשההזמנה משולמת', description: 'רק לאחר אישור התשלום' },
          { value: 'on_fulfilled', label: 'כשההזמנה נשלחת', description: 'כשההזמנה מסומנת כנשלחה בחנות' },
          { value: 'manual', label: 'ידני', description: 'בלחיצה מתוך יומן הפעולות' },
        ],
      },
      {
        key: 'document_type', type: 'choice', label: 'סוג המסמך', default: 'invoice_receipt',
        choices: [
          { value: 'tax_invoice', label: 'חשבונית מס' },
          { value: 'invoice_receipt', label: 'חשבונית מס / קבלה' },
          { value: 'delivery_note', label: 'תעודת משלוח' },
        ],
      },
      {
        key: 'customer_mode', type: 'choice', label: 'לקוח במסמך', default: 'general',
        choices: [
          { value: 'general', label: 'לקוח כללי אחד', description: 'כל ההזמנות על אותו לקוח' },
          { value: 'per_order', label: 'לפי פרטי ההזמנה', description: 'שיוך ללקוח הקונה' },
        ],
      },
      {
        key: 'general_customer_id', type: 'text', label: 'מספר הלקוח הכללי במערכת הניהול', default: '',
        placeholder: 'לדוגמה: 100', showWhen: { key: 'customer_mode', equals: 'general' },
      },
      {
        key: 'customer_match', type: 'choice', label: 'זיהוי לקוח קיים לפי', default: 'phone_or_email',
        showWhen: { key: 'customer_mode', equals: 'per_order' },
        choices: [
          { value: 'phone', label: 'טלפון' },
          { value: 'email', label: 'מייל' },
          { value: 'phone_or_email', label: 'טלפון או מייל' },
        ],
      },
      {
        key: 'create_missing_customer', type: 'toggle', label: 'ליצור לקוח חדש אם לא נמצא', default: true,
        showWhen: { key: 'customer_mode', equals: 'per_order' },
      },
      {
        key: 'shipping', type: 'choice', label: 'דמי משלוח', default: 'line_item',
        choices: [
          { value: 'line_item', label: 'כשורה נפרדת במסמך' },
          { value: 'exclude', label: 'לא לכלול' },
        ],
      },
      {
        key: 'discounts', type: 'choice', label: 'הנחות וקופונים', default: 'document',
        choices: [
          { value: 'document', label: 'הנחה כללית על המסמך' },
          { value: 'per_line', label: 'מחולקות על השורות' },
        ],
      },
      {
        key: 'vat_mode', type: 'choice', label: 'מחירים בחנות', default: 'from_store',
        choices: [
          { value: 'from_store', label: 'לפי הגדרת החנות' },
          { value: 'include', label: 'כולל מע"מ' },
          { value: 'exclude', label: 'לפני מע"מ' },
        ],
      },
      {
        key: 'refunds', type: 'choice', label: 'החזר כספי בחנות', default: 'ignore',
        choices: [
          { value: 'credit_note', label: 'להפיק חשבונית זיכוי' },
          { value: 'ignore', label: 'לא לעשות כלום' },
        ],
      },
      {
        key: 'send_to_customer', type: 'toggle', label: 'לשלוח את המסמך במייל ללקוח', default: false,
      },
      {
        key: 'warehouse_priority', type: 'warehouse_priority', label: 'סדר עדיפות מחסנים', default: [],
        help: 'גררו כדי לקבוע את הסדר. מחסן כבוי לא ישמש להורדת מלאי.',
      },
      {
        key: 'allocation_method', type: 'choice', label: 'איך לבחור מחסן להזמנה', default: 'consolidate',
        choices: [
          {
            value: 'consolidate', label: 'איחוד למחסן אחד',
            description: 'מעדיף מחסן אחד שיש בו את כל ההזמנה, גם אם הוא לא הראשון בסדר. לדוגמה: הוזמנו 5 יחידות, בסטודיו יש 3 ובהאנגר 10, הכול יורד מההאנגר בחשבונית אחת.',
          },
          {
            value: 'priority', label: 'לפי סדר העדיפות',
            description: 'לוקח קודם מהמחסן הראשון ומשלים מהבא אחריו, גם אם זה מפצל את ההזמנה. לדוגמה: 3 יורדות מהסטודיו ו-2 מההאנגר, ויוצאות 2 חשבוניות.',
          },
        ],
      },
      {
        key: 'shortage_mode', type: 'choice', label: 'כשאין מספיק מלאי באף מחסן', default: 'stop_and_alert',
        choices: [
          {
            value: 'stop_and_alert', label: 'לעצור ולהתריע',
            description: 'החשבונית לא יוצאת. תקבלו התראה, תבחרו מחסן לכל פריט במסך "הזמנות ממתינות" ותשדרו להפקה.',
          },
          {
            value: 'issue_anyway', label: 'להוריד בכל זאת',
            description: 'החשבונית יוצאת מיד, והחוסר יורד מהמחסן הראשון ברשימה. לדוגמה: בסטודיו 2 יחידות והוזמנו 5, הסטודיו יירד ל-3-.',
          },
        ],
      },
      {
        key: 'alert_email_enabled', type: 'toggle', label: 'התראה במייל על הזמנה שנעצרה', default: false,
        help: 'התראה בתוך המערכת (בפעמון) נשלחת תמיד.',
        showWhen: { key: 'shortage_mode', equals: 'stop_and_alert' },
      },
      {
        key: 'alert_email', type: 'text', inputType: 'email', label: 'כתובת מייל להתראות', default: '',
        placeholder: 'name@example.com', showWhen: { key: 'alert_email_enabled', equals: true },
      },
      {
        key: 'alert_sms_enabled', type: 'toggle', label: 'התראה ב-SMS על הזמנה שנעצרה', default: false,
        showWhen: { key: 'shortage_mode', equals: 'stop_and_alert' },
      },
      {
        key: 'alert_phone', type: 'text', inputType: 'tel', label: 'מספר טלפון להתראות', default: '',
        placeholder: '050-0000000', showWhen: { key: 'alert_sms_enabled', equals: true },
      },
    ],
  },
  {
    key: 'stock_to_store',
    title: 'עדכון מלאי בחנות',
    description: 'יתרות המלאי ממערכת הניהול מתעדכנות בחנות לפי מק"ט.',
    allowedDirections: ['erp_to_store'],
    status: 'available',
    options: [
      {
        key: 'frequency', type: 'choice', label: 'תדירות עדכון', default: 'hourly',
        choices: [
          { value: 'every_15_min', label: 'כל 15 דקות' },
          { value: 'hourly', label: 'כל שעה' },
          { value: 'daily', label: 'פעם ביום' },
          { value: 'manual', label: 'ידני' },
        ],
      },
      {
        key: 'match_by', type: 'choice', label: 'התאמת מוצרים לפי', default: 'sku',
        choices: [
          { value: 'sku', label: 'מק"ט' },
          { value: 'barcode', label: 'ברקוד' },
        ],
      },
      {
        key: 'stock_warehouses', type: 'warehouse_multi', label: 'מאילו מחסנים לסכם את המלאי', default: [],
        help: 'לכל מק"ט נשלח לחנות מספר אחד: סך המלאי במחסנים המסומנים. בלי סימון = כל המחסנים.',
      },
      {
        key: 'sku_filter_mode', type: 'choice', label: 'אילו מק"טים לעדכן', default: 'all',
        choices: [
          { value: 'all', label: 'כל המק"טים' },
          { value: 'exclude', label: 'הכל חוץ מהרשימה', description: 'המק"טים ברשימה לא יתעדכנו בחנות' },
          { value: 'include_only', label: 'רק מה שברשימה', description: 'רק המק"טים ברשימה יתעדכנו בחנות' },
        ],
      },
      {
        key: 'sku_list', type: 'sku_list', default: '',
        label: 'רשימת המק"טים',
        help: 'מק"ט בכל שורה או מופרדים בפסיק. אפשר לכתוב קידומת עם כוכבית בסוף, למשל W22-*, כדי לכלול את כל המק"טים שמתחילים כך.',
        showWhen: { key: 'sku_filter_mode', equals: ['exclude', 'include_only'] },
      },
      {
        key: 'safety_buffer', type: 'number', label: 'מרווח ביטחון', default: 0, min: 0, suffix: 'יחידות',
        help: 'כמות שתופחת מהמלאי המוצג בחנות',
      },
      {
        key: 'negative_as_zero', type: 'toggle', label: 'להציג מלאי שלילי כאפס', default: true,
      },
      {
        key: 'unmatched', type: 'choice', label: 'מוצר שלא נמצא בחנות', default: 'skip_and_log',
        choices: [
          { value: 'skip_and_log', label: 'לדלג ולרשום ביומן' },
          { value: 'skip_silently', label: 'לדלג בשקט' },
        ],
      },
    ],
  },
  {
    key: 'price_sync',
    title: 'עדכון מחירים',
    description: 'סנכרון מחירי מוצרים בין החנות למערכת הניהול.',
    allowedDirections: ['erp_to_store', 'store_to_erp'],
    status: 'coming_soon',
    options: [],
  },
  {
    key: 'customer_sync',
    title: 'סנכרון לקוחות',
    description: 'לקוחות חדשים בחנות נפתחים גם במערכת הניהול.',
    allowedDirections: ['store_to_erp', 'both'],
    status: 'coming_soon',
    options: [],
  },
  {
    key: 'fulfillment_status',
    title: 'עדכון סטטוס משלוח',
    description: 'סימון הזמנה כנשלחה בחנות כשהופק לה מסמך משלוח.',
    allowedDirections: ['erp_to_store'],
    status: 'coming_soon',
    options: [],
  },
];

export function flowByKey(key: string): FlowDef | undefined {
  return FLOWS.find(f => f.key === key);
}

export function defaultOptions(flow: FlowDef): FlowOptionValues {
  return Object.fromEntries(flow.options.map(o => [o.key, o.default]));
}

export function isOptionVisible(opt: FlowOptionDef, values: FlowOptionValues): boolean {
  if (!opt.showWhen) return true;
  const { key, equals } = opt.showWhen;
  const current = values[key];
  return Array.isArray(equals) ? equals.includes(String(current)) : current === equals;
}

export function parseSkuList(raw: string): { exact: string[]; prefixes: string[] } {
  const items = [...new Set(raw.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean))];
  return {
    exact: items.filter(s => !s.endsWith('*')),
    prefixes: items.filter(s => s.endsWith('*')).map(s => s.slice(0, -1)).filter(Boolean),
  };
}
